import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/axios';
import toast from 'react-hot-toast';
import {
  FiPlus,
  FiCamera,
  FiEdit,
  FiTrash2,
  FiEye,
  FiAlertTriangle,
  FiRefreshCw,
  FiImage,
  FiX,
  FiCheckCircle,
  FiInfo,
  FiCalendar,
  FiChevronRight,
  FiUpload
} from 'react-icons/fi';
import { Fancybox } from '@fancyapps/ui';
import '@fancyapps/ui/dist/fancybox/fancybox.css';


const DamageReports = () => {
  const { user } = useAuth();
  const [damageReports, setDamageReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingReport, setEditingReport] = useState(null);
  const [reservations, setReservations] = useState([]);
  const [reservationsLoading, setReservationsLoading] = useState(false);

  const [formData, setFormData] = useState({
    reservationId: '',
    beforePhotos: []
  });



  useEffect(() => {
    fetchDamageReports();
    fetchReservations();

    // Initialize Fancybox for image galleries
    Fancybox.bind('[data-fancybox]', {
      // Custom options
      loop: true,
      buttons: ['zoom', 'slideShow', 'fullScreen', 'thumbs', 'close'],
      animationEffect: 'fade',
      transitionEffect: 'slide',
      thumbs: {
        autoStart: false
      }
    });

    // Cleanup function
    return () => {
      Fancybox.destroy();
    };
  }, []);

  const fetchDamageReports = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/damage-reports');
      console.log('Damage reports response:', response.data);
      // L'API retourne { success: true, count: X, data: [...] }
      setDamageReports(response.data.data || []);
    } catch (error) {
      console.error('Error fetching damage reports:', error);
      toast.error('Erreur lors du chargement des rapports');
      setDamageReports([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchReservations = async () => {
    try {
      setReservationsLoading(true);
      console.log('Fetching reservations...');
      const response = await api.get('/api/reservations');
      console.log('Reservations response:', response.data);
      // L'API retourne { success: true, count: X, data: [...] }
      const reservations = response.data.data || [];
      // Filtrer seulement les réservations actives (non complétées)
      const activeReservations = Array.isArray(reservations)
        ? reservations.filter(reservation => reservation.status !== 'Completed')
        : [];
      setReservations(activeReservations);
    } catch (error) {
      console.error('Error fetching reservations:', error);
      toast.error('Erreur lors du chargement des réservations');
      setReservations([]);
    } finally {
      setReservationsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePhotoUpload = (e, type) => {
    const files = Array.from(e.target.files);
    setFormData(prev => ({
      ...prev,
      [`${type}Photos`]: [...prev[`${type}Photos`], ...files]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.reservationId) {
      toast.error('Veuillez sélectionner une réservation');
      return;
    }

    // For new reports, require at least one photo
    // For editing, allow keeping existing photos without adding new ones
    if (!editingReport && formData.beforePhotos.length === 0) {
      toast.error('Veuillez sélectionner au moins une photo avant');
      return;
    }

    try {
      const submitData = new FormData();

      // Add text fields
      submitData.append('reservationId', formData.reservationId);

      // Add photos
      formData.beforePhotos.forEach(photo => {
        submitData.append('beforePhotos', photo);
      });

      if (editingReport) {
        await api.put(`/api/damage-reports/${editingReport._id}`, submitData);
        toast.success('Photos avant mises à jour avec succès');
      } else {
        await api.post('/api/damage-reports', submitData);
        toast.success('Photos avant téléchargées avec succès');
      }

      resetForm();
      setShowModal(false);
      fetchDamageReports();
    } catch (error) {
      console.error('Error saving photos:', error);
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleEdit = (report) => {
    setEditingReport(report);
    setFormData({
      reservationId: report.reservationId?._id || report.reservationId || '',
      beforePhotos: []
    });
    setShowModal(true);
  };

  // Function to get existing photos for editing
  const getExistingPhotos = (report) => {
    if (!report || !report.beforePhotos) return [];
    return report.beforePhotos.map(photo => ({
      url: photo,
      isExisting: true
    }));
  };

  const handleDelete = async (reportId) => {
    const confirmationMessage =
      'Êtes-vous sûr de vouloir supprimer ce rapport de photos "Avant" ?\n\n' +
      '⚠️  ATTENTION : Cette action supprimera définitivement toutes les photos associées à cette réservation.\n\n' +
      'Note : Les photos sont également supprimées automatiquement si la réservation est supprimée ou terminée.';

    if (window.confirm(confirmationMessage)) {
      try {
        await api.delete(`/api/damage-reports/${reportId}`);
        toast.success('Rapport et photos supprimés avec succès');
        fetchDamageReports();
      } catch (error) {
        console.error('Error deleting damage report:', error);
        toast.error('Erreur lors de la suppression');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      reservationId: '',
      beforePhotos: []
    });
    setEditingReport(null);
  };

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };



  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <div className="relative">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-200 border-t-primary-600"></div>
          <FiCamera className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-5 h-5 text-primary-600" />
        </div>
        <p className="text-gray-500 dark:text-gray-400 animate-pulse">Chargement des photos avant...</p>
      </div>
    );
  }

  // Vérification de sécurité pour s'assurer que damageReports est un tableau
  if (!Array.isArray(damageReports)) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-sm border border-gray-100 dark:border-gray-700 text-center max-w-md">
          <div className="w-16 h-16 bg-gradient-to-br from-red-100 to-rose-100 dark:from-red-900/40 dark:to-rose-900/40 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <FiAlertTriangle className="w-8 h-8 text-red-600 dark:text-red-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Erreur de chargement</h3>
          <p className="text-gray-500 dark:text-gray-400 mb-6">Les données reçues ne sont pas dans le format attendu.</p>
          <button
            onClick={() => {
              setDamageReports([]);
              fetchDamageReports();
            }}
            className="px-5 py-2.5 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white font-medium rounded-xl shadow-lg shadow-primary-500/25 transition-all duration-300"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2 sm:gap-3">
            <div className="p-1.5 sm:p-2 bg-gradient-to-br from-primary-500 to-primary-700 rounded-lg sm:rounded-xl shadow-lg">
              <FiCamera className="w-5 h-5 sm:w-7 sm:h-7 text-white" />
            </div>
            Photos Avant
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
            Téléchargez des photos avant pour les voitures et clients
          </p>
        </div>

        <div className="flex flex-wrap gap-2 sm:gap-3">
          <button
            onClick={openCreateModal}
            className="group flex items-center gap-2 px-3 sm:px-5 py-2 sm:py-2.5 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white font-medium rounded-xl shadow-lg shadow-primary-500/25 hover:shadow-xl hover:shadow-primary-500/30 transition-all duration-300 hover:-translate-y-0.5 text-sm"
          >
            <FiPlus className="w-4 h-4 sm:w-5 sm:h-5 transition-transform group-hover:rotate-90 duration-300" />
            <span className="hidden sm:inline">Nouvelle Photo Avant</span>
            <span className="sm:hidden">Ajouter</span>
          </button>
          <button
            onClick={() => {
              fetchReservations();
              fetchDamageReports();
              toast.success('Données rafraîchies');
            }}
            className="flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 font-medium rounded-xl hover:bg-gray-200 dark:hover:bg-gray-700 transition-all text-sm"
            title="Rafraîchir les données"
          >
            <FiRefreshCw className="w-4 h-4" />
            <span className="hidden sm:inline">Rafraîchir</span>
          </button>
        </div>
      </div>

      {/* Info Alert Card */}
      <div className="bg-gradient-to-br from-primary-50 to-primary-100/50 dark:from-primary-900/20 dark:to-primary-800/10 border border-primary-200/50 dark:border-primary-700/30 rounded-2xl p-5 backdrop-blur-sm">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-white dark:bg-gray-800 rounded-xl shadow-sm">
            <FiInfo className="w-6 h-6 text-primary-600 dark:text-primary-400" />
          </div>
          <div className="flex-1">
            <h3 className="text-base font-semibold text-primary-900 dark:text-primary-100">
              Information Importante
            </h3>
            <div className="mt-2 text-sm text-primary-700 dark:text-primary-300">
              <p className="font-medium">Les photos sont automatiquement supprimées quand :</p>
              <ul className="mt-2 space-y-1">
                <li className="flex items-center gap-2">
                  <FiChevronRight className="w-4 h-4 text-primary-500" />
                  Une réservation est <strong>supprimée</strong> (immédiatement)
                </li>
                <li className="flex items-center gap-2">
                  <FiChevronRight className="w-4 h-4 text-primary-500" />
                  Une réservation est marquée comme <strong>terminée</strong> (+1 jour de délai)
                </li>
              </ul>
              <p className="mt-3 text-xs text-primary-600 dark:text-primary-400">
                Le délai de +1 jour permet de vérifier les photos avant suppression automatique.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Reports List */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
        {/* Table Header with count */}
        <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 bg-gradient-to-r from-primary-50 to-indigo-50 dark:from-primary-900/10 dark:to-indigo-900/10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary-100 dark:bg-primary-900/40 rounded-lg">
                <FiImage className="w-5 h-5 text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                  Galerie des Photos Avant
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {damageReports.length} enregistrement{damageReports.length > 1 ? 's' : ''}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900/50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                  Réservation
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                  Photos Avant
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {!Array.isArray(damageReports) || damageReports.length === 0 ? (
                <tr>
                  <td colSpan="4" className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-2xl flex items-center justify-center">
                        <FiCamera className="w-8 h-8 text-gray-400" />
                      </div>
                      <p className="text-gray-500 dark:text-gray-400 font-medium">
                        {!Array.isArray(damageReports) ? 'Erreur de chargement des données' : 'Aucune photo avant disponible'}
                      </p>
                      <p className="text-sm text-gray-400 dark:text-gray-500">
                        Commencez par ajouter des photos pour vos réservations
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                damageReports.map((report) => (
                  <tr key={report._id} className="hover:bg-gray-50 dark:hover:bg-gray-900/50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-primary-100 to-indigo-100 dark:from-primary-900/40 dark:to-indigo-900/40 rounded-xl flex items-center justify-center flex-shrink-0">
                          <FiCamera className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-gray-900 dark:text-white">
                            {report.reservationId ? (
                              <>
                                {report.reservationId.car ? (
                                  <>
                                    {report.reservationId.car.brand} {report.reservationId.car.model}
                                    {report.reservationId.car.licensePlate && (
                                      <span className="ml-2 px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-xs rounded-md font-mono">
                                        {report.reservationId.car.licensePlate}
                                      </span>
                                    )}
                                  </>
                                ) : (
                                  <span className="text-red-500">Voiture non trouvée</span>
                                )}
                              </>
                            ) : (
                              <span className="text-red-500">Réservation inconnue</span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            {report.reservationId?.client ? (
                              report.reservationId.client.name
                            ) : (
                              <span className="text-red-400">Client non trouvé</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 dark:text-white">
                        {report.beforePhotos && report.beforePhotos.length > 0 ? (
                          <div>
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-brand-green/10 text-brand-green text-xs font-medium rounded-lg mb-2">
                              <FiImage className="w-3 h-3" />
                              {report.beforePhotos.length} photo{report.beforePhotos.length > 1 ? 's' : ''}
                            </span>
                            <div className="flex flex-wrap gap-2 mt-2">
                              {report.beforePhotos.map((photo, index) => (
                                <div key={index} className="relative group">
                                  <a
                                    href={photo}
                                    data-fancybox={`gallery-${report._id}`}
                                    data-caption={`Photo ${index + 1} - ${report.reservationId?.car?.brand} ${report.reservationId?.car?.model}`}
                                    className="block"
                                  >
                                    <img
                                      src={photo}
                                      alt={`Photo avant ${index + 1}`}
                                      className="w-14 h-14 object-cover rounded-xl border-2 border-gray-200 dark:border-gray-600 hover:border-primary-500 dark:hover:border-primary-400 transition-all duration-200 cursor-pointer shadow-sm hover:shadow-lg hover:scale-105"
                                    />
                                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-200 rounded-xl flex items-center justify-center">
                                      <FiEye className="text-white opacity-0 group-hover:opacity-100 transition-all duration-200 w-4 h-4" />
                                    </div>
                                  </a>
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <span className="text-gray-400 dark:text-gray-500 text-xs">Aucune photo</span>
                        )}
                      </div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                        <FiCalendar className="w-4 h-4" />
                        {new Date(report.createdAt).toLocaleDateString('fr-FR')}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEdit(report)}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 hover:bg-primary-100 dark:hover:bg-primary-900/50 rounded-lg transition-colors text-sm font-medium"
                          title="Modifier"
                        >
                          <FiEdit className="w-4 h-4" />
                          <span className="hidden sm:inline">Modifier</span>
                        </button>
                        <button
                          onClick={() => handleDelete(report._id)}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/50 rounded-lg transition-colors text-sm font-medium"
                          title="Supprimer"
                        >
                          <FiTrash2 className="w-4 h-4" />
                          <span className="hidden sm:inline">Supprimer</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-primary-500 to-indigo-600 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 rounded-lg">
                    {editingReport ? <FiEdit className="w-5 h-5 text-white" /> : <FiCamera className="w-5 h-5 text-white" />}
                  </div>
                  <h3 className="text-xl font-bold text-white">
                    {editingReport ? 'Modifier les photos' : 'Nouvelles photos avant'}
                  </h3>
                </div>
                <button
                  onClick={closeModal}
                  className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors"
                >
                  <FiX className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Reservation Selection */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Réservation <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="reservationId"
                    value={formData.reservationId}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all text-gray-900 dark:text-white cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    required
                    disabled={reservationsLoading}
                  >
                    <option value="" className="text-gray-500">
                      {reservationsLoading ? 'Chargement des réservations...' : 'Sélectionner une réservation'}
                    </option>
                    {Array.isArray(reservations) && reservations.map(reservation => {
                      const carInfo = reservation.car ? `${reservation.car.brand} ${reservation.car.model} (${reservation.car.licensePlate || 'Plaque manquante'})` : 'Voiture inconnue';
                      const clientInfo = reservation.client ? reservation.client.name : 'Client inconnu';
                      const dateInfo = reservation.startDate ? new Date(reservation.startDate).toLocaleDateString('fr-FR') : 'Date inconnue';

                      return (
                        <option key={reservation._id} value={reservation._id} className="text-gray-900 dark:text-white">
                          {carInfo} - {clientInfo} ({dateInfo})
                        </option>
                      );
                    })}
                  </select>
                  {reservationsLoading && (
                    <div className="mt-2 flex items-center gap-2 text-sm text-gray-500">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary-200 border-t-primary-600"></div>
                      Chargement des réservations...
                    </div>
                  )}
                  {!reservationsLoading && reservations.length === 0 && (
                    <div className="mt-2 text-sm text-red-500 flex items-center gap-2">
                      <FiAlertTriangle className="w-4 h-4" />
                      Aucune réservation active disponible
                    </div>
                  )}
                </div>

                {/* Photo Uploads */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Photos Avant <span className="text-red-500">*</span>
                  </label>

                  {/* Existing Photos (for editing) */}
                  {editingReport && editingReport.beforePhotos && editingReport.beforePhotos.length > 0 && (
                    <div className="mb-4 p-4 bg-gray-50 dark:bg-gray-900 rounded-xl">
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-3 flex items-center gap-2">
                        <FiImage className="w-4 h-4" />
                        Photos existantes ({editingReport.beforePhotos.length})
                      </p>
                      <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
                        {editingReport.beforePhotos.map((photo, index) => (
                          <div key={index} className="relative group">
                            <a
                              href={photo}
                              data-fancybox={`edit-gallery-${editingReport._id}`}
                              className="block"
                            >
                              <img
                                src={photo}
                                alt={`Photo existante ${index + 1}`}
                                className="w-full h-20 object-cover rounded-xl border-2 border-gray-200 dark:border-gray-600 hover:border-primary-500 transition-colors cursor-pointer"
                              />
                            </a>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Upload Area */}
                  <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 hover:border-primary-400 dark:hover:border-primary-500 rounded-xl p-6 text-center transition-colors bg-gray-50/50 dark:bg-gray-900/50">
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={(e) => handlePhotoUpload(e, 'before')}
                      className="hidden"
                      id="before-photos"
                    />
                    <label htmlFor="before-photos" className="cursor-pointer block">
                      <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-primary-100 to-indigo-100 dark:from-primary-900/40 dark:to-indigo-900/40 rounded-2xl flex items-center justify-center">
                        <FiUpload className="w-8 h-8 text-primary-600 dark:text-primary-400" />
                      </div>
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {editingReport ? 'Ajouter de nouvelles photos' : 'Cliquez pour ajouter des photos'}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        PNG, JPG, JPEG jusqu'à 10MB
                      </p>
                    </label>
                  </div>

                  {/* Selected Photos Preview */}
                  {formData.beforePhotos.length > 0 && (
                    <div className="mt-4 p-4 bg-brand-green/5 dark:bg-brand-green/10 rounded-xl border border-brand-green/20">
                      <p className="text-sm font-medium text-brand-green mb-3 flex items-center gap-2">
                        <FiCheckCircle className="w-4 h-4" />
                        {formData.beforePhotos.length} photo{formData.beforePhotos.length > 1 ? 's' : ''} sélectionnée{formData.beforePhotos.length > 1 ? 's' : ''}
                      </p>
                      <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
                        {formData.beforePhotos.map((photo, index) => (
                          <div key={index} className="relative group">
                            <img
                              src={URL.createObjectURL(photo)}
                              alt={`Photo ${index + 1}`}
                              className="w-full h-20 object-cover rounded-xl border-2 border-brand-green/30"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                setFormData(prev => ({
                                  ...prev,
                                  beforePhotos: prev.beforePhotos.filter((_, i) => i !== index)
                                }));
                              }}
                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 transition-colors shadow-lg"
                              title="Supprimer cette photo"
                            >
                              <FiX className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Form Actions */}
                <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-700">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-5 py-2.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="px-5 py-2.5 bg-gradient-to-r from-primary-500 to-indigo-600 hover:from-primary-600 hover:to-indigo-700 text-white font-medium rounded-xl shadow-lg shadow-primary-500/25 transition-all duration-300 flex items-center gap-2"
                  >
                    <FiUpload className="w-4 h-4" />
                    {editingReport ? 'Mettre à jour' : 'Télécharger les photos'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DamageReports;
